import { HttpClient } from "@/app/api/HttpClient"
import { API_PREFIX_YOUTUBE_VIDEO_PATH } from "@/constants/prefix-path-api"
import { FilterSearchParams, VideoData, VideoListResponse } from "@/types/types"
import { message } from "antd"
import { t } from "i18next"
import { useState } from "react"

const youtubeVideoService = () => {
  const [isLoading, setIsLoading] = useState(false)

  const addVideo = async (data: { data: any }, silent: boolean = false) => {
    setIsLoading(true)
    try {
      const response = await HttpClient.post(`${API_PREFIX_YOUTUBE_VIDEO_PATH}`, data,{skipDomainFilter:true})
      return response
    } catch (error) {
      if (!silent) {
        message.error("Lỗi khi thêm video. Vui lòng thử lại.")
      }
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const fetchVideoById = async (id: number) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Invalid ID"))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.get(`${API_PREFIX_YOUTUBE_VIDEO_PATH}/${id}?populate=*`,{skipDomainFilter:true})
      return response?.data || null
    } catch (error) {
      message.error(t("Failed to fetch video data. Please try again"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updateVideo = async (id: number, data: { data: any }) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Invalid ID"))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.put(`${API_PREFIX_YOUTUBE_VIDEO_PATH}/${id}`, data,{skipDomainFilter:true})
      return response
    } catch (error) {
      message.error("Không thể cập nhật video. Vui lòng thử lại.")
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const deleteVideo = async (id: number) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Invalid ID"))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.delete(`${API_PREFIX_YOUTUBE_VIDEO_PATH}/${id}`,{skipDomainFilter:true})
      return response
    } catch (error) {
      message.error("Không thể cập nhật video. Vui lòng thử lại.")
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAllVideos = async (searchTerms: { [key: string]: string } = {}) => {
    const params: FilterSearchParams = {
      populate: "*",
      sort: "createdAt:desc",
      publicationState: "preview",
      filters: {},
      pagination: {
        pageSize: "100",
      },
      locale: "all",
    }

    const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page"]

    Object.keys(searchTerms).forEach(key => {
      if (!excludedKeys.includes(key)) {
        const value = searchTerms[key]

        if (key === "publicationState") {
          const convertParamStatusPost = value === "draft" || value === "all" ? "preview" : "live"
          params.publicationState = convertParamStatusPost

          if (value === "draft") {
            params.filters.publishedAt = { $null: true }
          } else {
            delete params.filters.publishedAt
          }
        } else if (key === "sort") {
          if (value) params.sort = value
        } else if (key === "locale") {
          params.locale = value
        } else if (!excludedKeys.includes(key)) {
          if (value) {
            params.filters[key] = { $containsi: value }
          }
        } else if (value) {
          params.pagination = {
            ...params.pagination,
            [key]: value,
          }
        }
      } else {
        const value = searchTerms[key]
        if (value) {
          params.pagination = {
            ...params.pagination,
            [key]: value,
          }
        }
      }
    })

    try {
      const response = await HttpClient.get<VideoListResponse>(`${API_PREFIX_YOUTUBE_VIDEO_PATH}`, { params,skipDomainFilter:true })
      if (!response) {
        return null
      }
      const modifiedResponse = response.data.map((video: VideoData) => ({
        ...video,
        status: video.attributes.publishedAt ? "published" : "draft",
        publishedAt: video.attributes.publishedAt,
      }))

      return {
        data: modifiedResponse,
        meta: response.meta,
      }
    } catch (error) {
      message.error(t("Failed to fetch videos. Please try again"))
      return null
    }
  }

  return {
    isLoading,
    addVideo,
    fetchVideoById,
    updateVideo,
    deleteVideo,
    fetchAllVideos,
  }
}

export default youtubeVideoService

